import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { debounce } from '@mui/material/utils';
import parse from 'autosuggest-highlight/parse';
import PropTypes from 'prop-types';
import { useController } from 'react-hook-form';

import getGoogleMapsAddressComponents from 'lib/googleMaps';

import { REQUIRED_FIELD } from '../../lib/form';
import GoogleMapsMapSkeleton from './GoogleMapsMapSkeleton';

const { REACT_APP_GOOGLE_MAPS_API_KEY, REACT_APP_GOOGLE_MAPS_API_URL } =
  process.env;
const AUTOCOMPLETE_LABEL = 'Ingresa la dirección de despacho';
const NO_OPTIONS_TEXT =
  'No hemos podido encontrar la dirección. Puedes ingresarla de nuevo';
const GOOGLE_AUTOCOMPLETE_COUNTRIES = ['cl', 'pe'];
const CLEAR_EVENT_TYPE = 'clear';
const INPUT_EVENT_TYPE = 'input';

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };
const placesService = { current: null };

function GoogleMapsAutocomplete({
  fieldName,
  setValue,
  disabled,
  control,
  showMap,
  getComputedAddress,
  initialCoordinates,
}) {
  const {
    field,
    formState: { errors },
  } = useController({
    name: fieldName,
    control,
    rules: { required: REQUIRED_FIELD },
  });
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [currentLatLng, setCurrentLatLng] = useState(null);
  const loaded = useRef(false);
  const refMap = useRef(null);
  const refPlacesAttribution = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const handleDragEndRef = useRef(null);
  handleDragEndRef.current = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setCurrentLatLng({ lat, lng });
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== window.google.maps.GeocoderStatus.OK || !results?.[0]) {
        return;
      }
      const place = results[0];
      const computedAddress = getGoogleMapsAddressComponents(place);
      getComputedAddress(computedAddress);
      setValue(fieldName, place.formatted_address);
    });
  };

  const placeMarker = useCallback((position) => {
    if (!map.current) {
      return;
    }
    if (!marker.current) {
      marker.current = new window.google.maps.Marker({
        position,
        map: map.current,
        draggable: true,
      });
      marker.current.addListener('dragend', (event) =>
        handleDragEndRef.current?.(event),
      );
    } else {
      marker.current.setPosition(position);
      if (marker.current.getMap() !== map.current) {
        marker.current.setMap(map.current);
      }
    }
    setCurrentLatLng({ lat: position.lat, lng: position.lng });
  }, []);

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        `${REACT_APP_GOOGLE_MAPS_API_URL}?key=${REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector('head'),
        'google-maps',
      );
    }

    loaded.current = true;
  }
  const fetch = useMemo(
    () =>
      debounce((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 400),
    [],
  );
  const onChange = useCallback(
    (_event, newValue, reason) => {
      if (reason === CLEAR_EVENT_TYPE) {
        // set value to '' instead of null.
        setValue(fieldName, '');
      } else {
        const placeId = newValue.place_id;

        placesService.current.getDetails(
          {
            placeId,
            fields: ['address_components', 'formatted_address', 'geometry'],
          },
          (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              const position = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              };
              if (!map.current) {
                map.current = new window.google.maps.Map(refMap.current, {
                  center: position,
                  zoom: 16,
                });
              } else {
                map.current.setCenter(position);
              }
              const computedAddress = getGoogleMapsAddressComponents(place);
              getComputedAddress(computedAddress);
              placeMarker(position);
            }
          },
        );
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(fieldName, newValue?.description);
      }
    },
    [options, fieldName],
  );
  const onInputChange = useCallback(
    (event, newInputValue, reason) => {
      setInputValue(newInputValue);
      if (reason === INPUT_EVENT_TYPE && !newInputValue) {
        setValue(fieldName, '');
      }
    },
    [fieldName, setValue],
  );
  const isOptionEqualToValue = useCallback((option, value) => {
    if (!value) {
      return false;
    }
    if (typeof option === 'object') {
      return option.description === value;
    }
    return option === value;
  }, []);

  useEffect(() => {
    let active = true;
    if (
      !placesService.current &&
      window.google &&
      refPlacesAttribution.current
    ) {
      placesService.current = new window.google.maps.places.PlacesService(
        refPlacesAttribution.current,
      );
    }
    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (!inputValue) {
      setOptions(field.value ? [field.value] : []);
      return undefined;
    }

    fetch(
      {
        input: inputValue,
        componentRestrictions: {
          country: GOOGLE_AUTOCOMPLETE_COUNTRIES,
        },
      },
      (results) => {
        if (active) {
          let newOptions = [];

          if (field.value) {
            newOptions = [field.value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      },
    );

    return () => {
      active = false;
    };
  }, [field.value, inputValue, fetch]);

  // When a suggested address is pre-loaded (e.g. editing an order), draw the
  // map centered on its coordinates so the user sees the suggestion without
  // having to search again. GeoJSON coordinates are [longitude, latitude].
  // If coordinates aren't available, fall back to geocoding the current label
  // so the map still renders.
  useEffect(() => {
    if (!showMap || map.current || !refMap.current) {
      return undefined;
    }
    if (!initialCoordinates && !field.value) {
      return undefined;
    }
    let cancelled = false;
    const createMapAt = (position) => {
      if (cancelled || map.current || !refMap.current) {
        return;
      }
      map.current = new window.google.maps.Map(refMap.current, {
        center: position,
        zoom: 16,
      });
      placeMarker(position);
    };
    const drawInitialMap = () => {
      if (cancelled || map.current || !window.google?.maps || !refMap.current) {
        return;
      }
      if (initialCoordinates && initialCoordinates.length === 2) {
        createMapAt({
          lat: initialCoordinates[1],
          lng: initialCoordinates[0],
        });
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: field.value }, (results, status) => {
        if (
          cancelled ||
          status !== window.google.maps.GeocoderStatus.OK ||
          !results?.[0]?.geometry?.location
        ) {
          return;
        }
        const { location } = results[0].geometry;
        createMapAt({ lat: location.lat(), lng: location.lng() });
      });
    };
    if (window.google?.maps) {
      drawInitialMap();
      return undefined;
    }
    const intervalId = setInterval(() => {
      if (window.google?.maps) {
        clearInterval(intervalId);
        drawInitialMap();
      }
    }, 200);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [showMap, initialCoordinates, field.value]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Autocomplete
        id="google-maps-autocomplete"
        sx={{ flexGrow: 1 }}
        size="small"
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.description
        }
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={field.value || ''}
        noOptionsText={NO_OPTIONS_TEXT}
        onChange={onChange}
        onInputChange={onInputChange}
        disableClearable={disabled}
        isOptionEqualToValue={isOptionEqualToValue}
        popupIcon={disabled ? null : <ArrowDropDownOutlinedIcon />}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            disabled={disabled}
            helperText={
              errors && errors[fieldName] && errors[fieldName].message
            }
            error={Boolean(!field.value && errors[fieldName])}
            label={AUTOCOMPLETE_LABEL}
            fullWidth
            inputRef={field.ref}
          />
        )}
        renderOption={(props, option) => {
          const matches =
            option.structured_formatting?.main_text_matched_substrings || [];

          const parts = parse(
            option.structured_formatting?.main_text,
            matches.map((match) => [match.offset, match.offset + match.length]),
          );

          return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <li {...props}>
              <Grid container alignItems="center">
                <Grid item sx={{ display: 'flex', width: 44 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                </Grid>
                <Grid
                  item
                  sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}
                >
                  {parts.map((part, index) => (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      component="span"
                      sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                    >
                      {part.text}
                    </Box>
                  ))}

                  <Typography variant="body2" color="text.secondary">
                    {option.structured_formatting?.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
      <div ref={refPlacesAttribution} style={{ display: 'none' }} />
      <Box>
        <div
          ref={refMap}
          id="map"
          hidden={!showMap || (showMap && !field.value)}
          style={{ height: '300px', marginBottom: 4 }}
        />
        {showMap && field.value && currentLatLng && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'right' }}
          >
            {`Lat ${currentLatLng.lat.toFixed(
              6,
            )} · Lng ${currentLatLng.lng.toFixed(6)}`}
          </Typography>
        )}
      </Box>
      {showMap && !field.value && <GoogleMapsMapSkeleton />}
    </Box>
  );
}
GoogleMapsAutocomplete.propTypes = {
  fieldName: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.shape({ description: PropTypes.string }),
    PropTypes.string,
  ]),
  setValue: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  control: PropTypes.object.isRequired,
  showMap: PropTypes.bool,
  getComputedAddress: PropTypes.func,
  initialCoordinates: PropTypes.arrayOf(PropTypes.number),
};
GoogleMapsAutocomplete.defaultProps = {
  value: null,
  disabled: false,
  showMap: false,
  getComputedAddress: () => {},
  initialCoordinates: null,
};
export default GoogleMapsAutocomplete;
